import React from 'react';
import moment from 'moment';
import {
  Button,
  Gap,
  Input,
  LabelValue,
  Spinner,
  Tab,
  TabBar,
  Table,
  Tabs,
  TabView,
} from '@salecycle/ui-component-library';
import { Container } from 'components/experimental/Container/Container';
import { Section } from 'components/experimental/Section/Section';
import { NotFoundPage } from 'core/ui/components/error';
import { useFeatureFlags } from 'core/app/AppContext';
import { Alert2 } from 'components/experimental/Alert/Alert2';
import { LegacyCard } from 'components/experimental/Card/LegacyCard';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useCheckCanSendMutation } from '../mutations/checkCanSendMutation';
import { emailValidCheck } from 'sections/user-management/utils/helpers';
import { StandardMainArea } from 'components/experimental/StandardMainArea';

const DATETIME_DISPLAY_FORMAT = 'MMMM Do, YYYY hh:mmA';

export function ConsentHistoryPage() {
  const emailQueryParam = new URLSearchParams(window.location.search).get('email');
  const { siteId } = useParams<{ siteId: string }>();
  const [emailValid, setEmailValid] = React.useState(true);
  const { flagEnabled } = useFeatureFlags();
  const { t } = useTranslation('compliance');
  const [searchDone, setSearchDone] = React.useState(false);
  const [emailAddress, setEmailAddress] = React.useState(emailQueryParam || '');
  const checkCanSendMutation = useCheckCanSendMutation(siteId, emailAddress);

  /*
    When opening this page with an email address already provided, invoke the
    mutation automatically rather than waiting for user to press Search button.
  */
  React.useEffect(() => {
    if (emailQueryParam && !searchDone) {
      checkCanSendMutation.mutate();
      setSearchDone(true);
    }
  }, [checkCanSendMutation, emailQueryParam, searchDone]);

  const handleSearchButtonClicked = () => {
    if (!emailAddress) {
      return;
    }
    const emailIsValid = emailValidCheck(emailAddress);
    setEmailValid(emailIsValid);
    if (emailIsValid) {
      checkCanSendMutation.mutate();
      setSearchDone(true);
    }
  };
  const consentHistory = checkCanSendMutation?.data?.data?.history?.sort((a, b) => (a.time < b.time ? 1 : -1));
  const expired = checkCanSendMutation?.data?.data?.expired;

  if (flagEnabled('compliance-consent-history')) {
    return (
      <StandardMainArea heading={t('consentHistoryPage.heading')} subHeading={t('consentHistoryPage.subHeading')}>
        <Gap top={30} />
        <Section maxWidth="1024px">
          <Input
            type="email"
            value={emailAddress}
            onChange={v => setEmailAddress(v)}
            spacingBelow={false}
            ariaLabel={''}
            placeholder="Enter email address here..."
            validationMessage={!emailValid ? t('consentHistoryPage.validEmailAddress') : ''}
          />
          <Container align="left" padding="5px 0px">
            <Button variation="secondary" onClick={handleSearchButtonClicked}>
              Search
            </Button>
          </Container>
        </Section>
        <Gap top={30} />
        {searchDone ? (
          <>
            <Section>
              <Tabs>
                <TabBar>
                  <Tab tabId="latest">{t('consentHistoryPage.tabs.latest')}</Tab>
                  <Tab tabId="history">{t('consentHistoryPage.tabs.history')}</Tab>
                </TabBar>
                <TabView tabId="latest">
                  {checkCanSendMutation.isLoading && (
                    <Container padding="50px">
                      <Spinner />
                    </Container>
                  )}
                  {checkCanSendMutation.isSuccess && (
                    <Container padding="30px 0px">
                      {consentHistory && consentHistory.length > 0 ? (
                        <LegacyCard>
                          <LabelValue
                            label={t('consentHistoryPage.latestCard.status')}
                            value={consentHistory[0].type}
                          />
                          <Gap top={10} />
                          <LabelValue
                            label={t('consentHistoryPage.latestCard.time')}
                            value={moment(consentHistory[0].time).format(DATETIME_DISPLAY_FORMAT)}
                          />
                          <Gap top={10} />
                          <LabelValue
                            label={t('consentHistoryPage.latestCard.source')}
                            value={consentHistory[0].source}
                          />
                          <Gap top={10} />
                          <LabelValue
                            label={t('consentHistoryPage.latestCard.expired')}
                            value={
                              expired
                                ? t('consentHistoryPage.latestCard.expiredYes')
                                : t('consentHistoryPage.latestCard.expiredNo')
                            }
                          />
                        </LegacyCard>
                      ) : (
                        <LegacyCard>
                          <LabelValue label={t('consentHistoryPage.latestCard.status')} value="No Status" />
                        </LegacyCard>
                      )}
                    </Container>
                  )}
                </TabView>
                <TabView tabId="history">
                  {checkCanSendMutation.isLoading && (
                    <Container padding="50px">
                      <Spinner />
                    </Container>
                  )}
                  {checkCanSendMutation.isSuccess && (
                    <Table
                      showPagination={false}
                      pageSize={100}
                      columns={[
                        {
                          Header: t('consentHistoryPage.table.columnHeaders.status'),
                          accessor: 'type',
                          Cell: StatusCell,
                        },
                        {
                          Header: t('consentHistoryPage.table.columnHeaders.time'),
                          accessor: 'time',
                          Cell: TimeCell,
                        },
                        {
                          Header: t('consentHistoryPage.table.columnHeaders.source'),
                          accessor: 'source',
                        },
                      ]}
                      data={consentHistory}
                    />
                  )}
                </TabView>
              </Tabs>
            </Section>
          </>
        ) : null}
        <Gap top={100} />
        {checkCanSendMutation.isError ? (
          <Section>
            <Alert2 heading={t('consentHistoryPage.canSendCheckErrorHeading')} variation="error">
              {t('consentHistoryPage.canSendCheckErrorMessage')}
            </Alert2>
          </Section>
        ) : null}
        <Gap top={30} />
      </StandardMainArea>
    );
  } else {
    return <NotFoundPage />;
  }
}

const TimeCell = props => <span>{moment(props.value).format(DATETIME_DISPLAY_FORMAT)}</span>;
const StatusCell = props => <span>{props.value}</span>;
